import React, {useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {DragDropContext, Droppable} from "react-beautiful-dnd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/free-solid-svg-icons";

// Hooks
import useApi from "../../hooks/useApi";
import usePrevious from "../../hooks/usePrevious";
import useVisibilityConflicts from "../../hooks/useVisibilityConflicts";

// Contexts
import {AuthContext} from "../../contexts/auth";

// Components
import SearchSelect from "../../components/SearchSelect";
import Modal from "../../components/Modal";
import Help from "../../components/Help";
import RenderChecksheetBuilder from "./RenderChecksheetBuilder";
import VisibilityConflictWarning from "./VisibilityConflictWarning";
import {InputError, InputRadioGroup, InputText} from "../../components/form/FormInputs";

// Utils
import {deepCopyBuilder, FIELD, newFromBuilder, validBuilder} from "../../utils/builder";

// Style
import {flex} from "../../style/components/mixins";
import {pad, radius} from "../../style/components/variables";
import {
  Abbr,
  Button,
  Error,
  FormField,
  FormGroup,
  Heading,
  Inline,
  Form,
  Pill,
  Text
} from "../../style/components/general";

const defaultValues = {
  label: "",
  mode: "User",
  members: [],
  state: "default"
};

const withEmailOptionLabel = user => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  let optionLabel = name;
  if (user.email) optionLabel = name ? `${name} (${user.email})` : user.email;
  return {...user, optionLabel};
};

const schema = yup.object().shape({
  label: yup.string().nullable().required("Please provide a label."),
  members: yup
    .array()
    .nullable()
    .test({
      test: val => Array.isArray(val) && val.length > 0,
      message: "Please provide restriction members."
    })
});

const recurseCopy = (ids, currentById, byId, allIds, level = 0) => {
  let hasFields = false;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const item = currentById[id];
    if (item) {
      if (item.element === FIELD && !item.stale && !item.deleted) {
        byId[id] = {...item};
        if (level === 0) allIds.push(id);
        hasFields = true;
      } else if (item.children?.length && item.toggle) {
        const result = recurseCopy(item.children, currentById, byId, allIds, level + 1);
        if (result) {
          hasFields = true;
          byId[id] = {...item, children: [...item.children]};
          if (level === 0) allIds.push(id);
        }
      }
    }
  }
  return hasFields;
};

const copyActiveFields = builder => {
  const {byId, allIds} = deepCopyBuilder(builder);
  const newById = {};
  const newAllIds = [];
  recurseCopy(allIds, byId, newById, newAllIds);
  return {byId: newById, allIds: newAllIds};
};

const ModalChecksheetRestriction = ({
  visible,
  setVisible,
  goBack,
  target,
  facilityId,
  states,
  update,
  state,
  restrictTo = []
}) => {
  const {roles} = useContext(AuthContext);

  const {api: apiUser} = useApi("facility-users");

  const defaultBuilder = useMemo(() => states["0"]?.builder || {byId: {}, allIds: []}, [states]);

  const stateBuilder = useMemo(
    () => (state ? states[state.id]?.builder : undefined),
    [state, states]
  );

  const expandBuilder = (subset, builder) => {
    if (!builder?.byId || !subset?.byId) return builder;

    const {byId, allIds} = builder;

    const newById = Object.fromEntries(
      Object.entries(byId)
        .filter(([, val]) => !val.stale)
        .map(([key, val]) => [
          key,
          {
            ...val,
            toggle: key in subset.byId
          }
        ])
    );

    return {byId: newById, allIds};
  };

  const initialMemberType = useMemo(
    () => (target?.members?.[0]?.label ? "Role" : "User"),
    [target]
  );

  const [memberResults, setMemberResults] = useState([]);
  const [builderError, setBuilderError] = useState();
  const [copiedBuilder, setCopiedBuilder] = useState(
    target
      ? expandBuilder(target.builder, target.isState ? stateBuilder : defaultBuilder)
      : copyActiveFields(states["0"]?.builder || {byId: {}, allIds: []})
  );

  const builderSubset = useMemo(() => {
    const filteredBuilder = {allIds: [], byId: {}};
    newFromBuilder(copiedBuilder.allIds, copiedBuilder, filteredBuilder, true);
    return filteredBuilder;
  }, [copiedBuilder]);

  const form = useForm({
    defaultValues: target
      ? {
          label: target.label,
          members: target.members.map(member =>
            initialMemberType === "User"
              ? {
                  publicId: member.publicId,
                  email: member.email,
                  firstName: member.firstName,
                  lastName: member.lastName
                }
              : {id: member.id, label: member.label}
          ),
          mode: initialMemberType,
          id: target.id,
          state: target.isState ? "state" : "default"
        }
      : defaultValues,
    resolver: yupResolver(schema)
  });

  const {
    setValue,
    watch,
    handleSubmit,
    trigger,
    formState: {errors, submitCount}
  } = form;

  const watchMode = watch("mode");
  const watchMembers = watch("members");
  const watchState = watch("state");
  const watchLabel = watch("label");

  const draftRestrictions = useMemo(
    () => [{label: watchLabel || "new", members: watchMembers}],
    [watchLabel, watchMembers]
  );
  const conflicts = useVisibilityConflicts(restrictTo, draftRestrictions, facilityId);
  const [confirmConflicts, setConfirmConflicts] = useState(false);

  useEffect(() => {
    if (!conflicts.length) setConfirmConflicts(false);
  }, [conflicts]);

  useEffect(() => {
    if (watchState === "default" && !target) {
      setCopiedBuilder(copyActiveFields(defaultBuilder));
    } else if (watchState === "state" && stateBuilder && !target) {
      setCopiedBuilder(copyActiveFields(stateBuilder));
    }
  }, [watchState, target, stateBuilder, defaultBuilder]);

  const prevMode = usePrevious(watchMode);

  useEffect(() => {
    if (submitCount > 0) {
      const valid = validBuilder(builderSubset.allIds, builderSubset);

      const errorMessage =
        "Invalid selection, please make sure all Components and Groups have at least one field or there is one checksheet level field selected.";
      setBuilderError(!valid ? errorMessage : undefined);
    }
  }, [submitCount, builderSubset]);

  const handleSave = ({label, members, id, state: targetState}, confirmed = false) => {
    const valid = validBuilder(builderSubset.allIds, builderSubset);

    const errorMessage =
      "Invalid selection, please make sure all Components and Groups have at least one field or there is one checksheet level field selected.";
    if (!valid) setBuilderError(errorMessage);
    else if (conflicts.length > 0 && !confirmed) {
      setBuilderError(undefined);
      setConfirmConflicts(true);
    } else {
      setBuilderError(undefined);

      update({
        id,
        label,
        members,
        builder: builderSubset,
        isState: targetState === "state"
      });

      goBack();
    }
  };

  useEffect(() => {
    if (prevMode && prevMode !== watchMode) setValue("members", []);
  }, [prevMode, watchMode, setValue]);

  useEffect(() => {
    if (submitCount > 0) trigger("members");
  }, [watchMembers, trigger, submitCount]);

  const handleSearchUser = query => {
    const filter = {
      Omit: watchMembers.map(({publicId}) => publicId),
      showHidden: true
    };

    if (query) filter.Search = query;

    const params = {
      facilityId,
      noTest: true,
      limit: query ? 5 : 20,
      filter: JSON.stringify(filter)
    };

    apiUser.callGet("", params).then(({status, data}) => {
      if (status === 200 && data) setMemberResults(data.map(withEmailOptionLabel));
    });
  };

  const handleSearchRole = query => {
    if (query)
      setMemberResults(
        roles?.filter(
          role =>
            !watchMembers.some(({label}) => label === role.label) &&
            role.label.toLowerCase().includes(query?.toLowerCase())
        ) || []
      );
    else
      setMemberResults(
        roles?.filter(role => !watchMembers.some(({label}) => label === role.label)) || []
      );
  };

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton={!!goBack}
      goBack={goBack}
      // fixedHeight
      // allowScroll={false}
    >
      <HeaderWrapper>
        <ModalTitle>Checksheet Restriction</ModalTitle>&nbsp;
        <Help title="Learn more">
          A checksheet restriction limits who can view and complete a subset of fields on this
          checksheet. Select the fields to include on the right, then choose the users or roles
          who should be restricted to them. Fields left unselected remain visible to everyone
          with access to the checksheet. Example: A restriction,  for
        </Help>
      </HeaderWrapper>

      <Inline fullWidth fullHeight>
        <ColLeft>
          <FormProvider {...form}>
            <Form onSubmit={handleSubmit(values => handleSave(values))} noValidate>
              <FormGroup>
                {state && !target && (
                  <FormField>
                    <InputRadioGroup
                      name="state"
                      label="State"
                      options={[
                        {label: "DEFAULT", value: "default"},
                        {label: state.name, value: "state"}
                      ]}
                      disabled={!!target}
                    />
                  </FormField>
                )}
                {state && target && (
                  <Inline>
                    Selected:{" "}
                    <Pill
                      color={
                        target.isState && state.color
                          ? `#${state.color}`
                          : undefined
                      }>
                      {target.isState ? state.name : "DEFAULT"}
                    </Pill>
                  </Inline>
                )}
                <FormField>
                  <InputText name="label" placeholder="Enter a label for the restriction..." required />
                </FormField>
                <FormField>
                  <InputRadioGroup name="mode" options={["User", "Role"]} />
                </FormField>
                <FormField>
                  {watchMode === "User" ? (
                    <SearchSelect
                      label="Members"
                      placeholder="Search users..."
                      results={memberResults}
                      setResults={setMemberResults}
                      search={handleSearchUser}
                      add={r =>
                        setValue("members", [
                          ...watchMembers,
                          {
                            email: r.email,
                            publicId: r.publicId,
                            firstName: r.firstName,
                            lastName: r.lastName
                          }
                        ])
                      }
                      showAll
                    />
                  ) : (
                    <SearchSelect
                      label="Members"
                      placeholder="Search roles..."
                      results={memberResults}
                      setResults={setMemberResults}
                      search={handleSearchRole}
                      add={r => setValue("members", [...watchMembers, {label: r.label, id: r.id}])}
                      showAll
                    />
                  )}
                  <InputError errors={errors} name="members" />
                </FormField>

                <MembersWrapper>
                  {watchMembers?.map(
                    ({email, label: roleLabel, firstName, lastName, id, publicId}) => {
                      const display = firstName
                        ? `${firstName} ${lastName}`
                        : roleLabel || email;
                      const tooltip =
                        email && display !== email ? `${display} (${email})` : display;
                      return (
                        <MemberPill key={`member-${id || publicId}`}>
                          <Abbr title={tooltip?.toUpperCase()}>{display?.toUpperCase()}</Abbr>
                          <IconButton
                            type="button"
                            onClick={() =>
                              setValue(
                                "members",
                                watchMembers.filter(curr => {
                                  if (email && curr.email !== email) return true;
                                  if (roleLabel && curr.label !== roleLabel) return true;
                                  return false;
                                })
                              )
                            }>
                            <FontAwesomeIcon icon={faClose} />
                          </IconButton>
                        </MemberPill>
                      );
                    }
                  )}
                </MembersWrapper>
                <VisibilityConflictWarning conflicts={conflicts} />
              </FormGroup>

              {confirmConflicts ? (
                <>
                  <Text>Save anyway?</Text>
                  <Inline>
                    <Submit
                      type="button"
                      onClick={handleSubmit(values => handleSave(values, true))}>
                      Save anyway
                    </Submit>
                    <Submit type="button" onClick={() => setConfirmConflicts(false)}>
                      Back
                    </Submit>
                  </Inline>
                </>
              ) : (
                <Submit type="submit">Create Restriction</Submit>
              )}
            </Form>
          </FormProvider>
        </ColLeft>
        <ColRight>
          {builderError && <Error>{builderError}</Error>}
          <DragDropContext onDragStart={() => {}} onDragEnd={() => {}}>
            <Droppable droppableId="checksheet.builder" isDropDisabled>
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  <RenderChecksheetBuilder
                    builder={copiedBuilder}
                    setBuilder={setCopiedBuilder}
                    toggleOnly
                    showEmptyMessage>
                    {provided.placeholder}
                  </RenderChecksheetBuilder>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </ColRight>
      </Inline>
    </Modal>
  );
};

ModalChecksheetRestriction.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  states: PropTypes.objectOf(PropTypes.any).isRequired,
  update: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  facilityId: PropTypes.number.isRequired,
  target: PropTypes.objectOf(PropTypes.any),
  state: PropTypes.objectOf(PropTypes.any),
  restrictTo: PropTypes.arrayOf(PropTypes.any)
};

// Style Overrides
const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: ${pad}px 0;
`;

const ModalTitle = styled(Heading)`
  margin: 0;
`;

const Col = styled.div`
  width: 100%;
  align-self: start;
`;

const ColLeft = styled(Col)`
  /* width: calc(40% - 5px); */
  margin-top: -${pad}px;
`;

const ColRight = styled(Col)`
  /* width: calc(60% - 5px);
  overflow-y: scroll;
  height: calc(100% - 80px); */
  padding-bottom: 20px;
  padding-left: ${pad}px;
`;

const Submit = styled(Button)`
  margin-bottom: ${pad}px;
`;

const MembersWrapper = styled(Inline)`
  flex-direction: column;
  align-items: start;
`;

const MemberPill = styled.div`
  ${flex("row", "nowrap", "space-between", "center")};
  width: min-content;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background-color: ${({theme}) => theme.secondary};
  padding: ${pad / 2}px ${pad}px;

  ${Abbr} {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const IconButton = styled(Button)`
  background-color: transparent;
  width: min-content;
  padding: 0;
  margin-left: ${pad / 2}px;

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

export default ModalChecksheetRestriction;
